import {
  ManageOwnershipDialog,
  proposedRepoToOwnershipEntity,
} from '@internal/backstage-plugin-common-library-react';
import React, { useState, useMemo } from 'react';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Button,
  makeStyles,
  Link,
  TablePagination,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';

import { ReviseOwnershipModal } from './ReviseOwnershipModal';
import { useProposedOwnershipRepos } from './hooks/useProposedOwnershipRepos';
import { ProposedOwnershipRepo } from './types';

const useStyles = makeStyles(theme => ({
  card: {
    boxShadow: theme.shadows[1],
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  cardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
  },
  statsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  statsLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statsText: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  statsCount: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    padding: '2px 8px',
    borderRadius: 10,
  },
  bulkAcceptButton: {
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '8px 20px',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: theme.palette.action.hover,
  },
  tableHeaderCell: {
    padding: theme.spacing(1, 1.5),
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: 'left',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tableCell: {
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13,
    color: theme.palette.text.primary,
    verticalAlign: 'middle',
  },
  entityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  entityIcon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.secondary.contrastText,
    flexShrink: 0,
  },
  entityInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  entityLink: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  entityLinkIcon: {
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
    },
  },
  teamTag: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
  },
  acceptButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.primary.main,
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  reviseButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  bulkReviseButton: {
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    padding: '8px 20px',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  checkbox: {
    color: theme.palette.text.disabled,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
  paginationContainer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  pagination: {
    border: 'none',
    '& .MuiTablePagination-toolbar': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiTablePagination-selectRoot': {
      marginRight: theme.spacing(2),
    },
  },
  filterSection: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  searchField: {
    flex: '1 1 300px',
    minWidth: 250,
  },
  filterControl: {
    flex: '1 1 200px',
    minWidth: 150,
  },
  filterChips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  filterChip: {
    height: 24,
    fontSize: 12,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    gap: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  emptyText: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const buildCatalogUrl = (entityRef: string): string => {
  const [kind, rest] = entityRef.split(':');
  const [namespace, name] = (rest || '').split('/');
  return `/catalog/${namespace || 'default'}/${kind}/${name}`;
};

const getTooltipText = (
  source: 'catalog' | 'watchtower',
  proposedByEmail?: string,
  recommendationType?: string,
): string => {
  if (source === 'catalog') {
    return 'Based on teams activity';
  }

  if (recommendationType === 'MARK_UNKNOWN') {
    return proposedByEmail
      ? `Marked as unknown by ${proposedByEmail}`
      : 'Marked as unknown';
  }

  return proposedByEmail
    ? `Proposed by ${proposedByEmail}`
    : 'Proposed by watchtower';
};

export const ActionRequired = () => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const { repos, loading, error, refetch } = useProposedOwnershipRepos();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [reviseModalOpen, setReviseModalOpen] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [repositoriesToRevise, setRepositoriesToRevise] = useState<
    ProposedOwnershipRepo[]
  >([]);
  const [repositoriesToAccept, setRepositoriesToAccept] = useState<
    ProposedOwnershipRepo[]
  >([]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  // Get unique teams from repos for filter dropdown
  const availableTeams = useMemo(() => {
    const teams = new Set(repos.map(r => r.teamRecommendation));
    return Array.from(teams).sort();
  }, [repos]);

  // Filter repos based on search and filters
  const filteredRepos = useMemo(() => {
    return repos.filter(repo => {
      // Search filter (repository name or entity name)
      const matchesSearch =
        searchQuery === '' ||
        repo.repositoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        repo.entity.toLowerCase().includes(searchQuery.toLowerCase());

      // Team filter
      const matchesTeam =
        selectedTeams.length === 0 ||
        selectedTeams.includes(repo.teamRecommendation);

      return matchesSearch && matchesTeam;
    });
  }, [repos, searchQuery, selectedTeams]);

  // Paginate filtered data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredRepos.slice(startIndex, endIndex);
  }, [filteredRepos, page, rowsPerPage]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageIds = paginatedData.map(item => item.id);
      setSelectedItems(
        prev => new Set([...Array.from(prev), ...currentPageIds]),
      );
    } else {
      const currentPageIds = new Set(paginatedData.map(item => item.id));
      setSelectedItems(
        prev => new Set(Array.from(prev).filter(id => !currentPageIds.has(id))),
      );
    }
  };

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleAccept = (id: string) => {
    const repo = repos.find(r => r.id === id);
    if (!repo) return;

    setRepositoriesToAccept([repo]);
    setAcceptDialogOpen(true);
  };

  const handleBulkAccept = () => {
    const selectedRepos = filteredRepos.filter(r => selectedItems.has(r.id));
    if (selectedRepos.length === 0) return;

    setRepositoriesToAccept(selectedRepos);
    setAcceptDialogOpen(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedTeams([]);
    setPage(0);
  };

  const handleCloseAcceptDialog = () => {
    setAcceptDialogOpen(false);
    setRepositoriesToAccept([]);
  };

  const handleAcceptSuccess = () => {
    setSelectedItems(new Set());
    refetch();
  };

  const handleRevise = (id: string) => {
    const repo = repos.find(r => r.id === id);
    if (!repo) return;

    setRepositoriesToRevise([repo]);
    setReviseModalOpen(true);
  };

  const handleBulkRevise = () => {
    const selectedRepos = filteredRepos.filter(r => selectedItems.has(r.id));
    if (selectedRepos.length === 0) return;

    setRepositoriesToRevise(selectedRepos);
    setReviseModalOpen(true);
  };

  const handleCloseReviseModal = () => {
    setReviseModalOpen(false);
    setRepositoriesToRevise([]);
  };

  const handleRevisionSuccess = (revisedCount: number) => {
    // Show success message
    alertApi.post({
      message: `Successfully revised ${revisedCount} repositor${
        revisedCount === 1 ? 'y' : 'ies'
      }`,
      severity: 'success',
      display: 'transient',
    });

    // Refetch the list to show updated data
    refetch();

    // Clear selection
    setSelectedItems(new Set());
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentPageSelectedCount = paginatedData.filter(item =>
    selectedItems.has(item.id),
  ).length;
  const allCurrentPageSelected =
    paginatedData.length > 0 &&
    currentPageSelectedCount === paginatedData.length;
  const someCurrentPageSelected =
    currentPageSelectedCount > 0 && !allCurrentPageSelected;

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography>Loading ownership recommendations...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.errorText}>
            Error loading recommendations: {error.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (repos.length === 0) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.header}>
            <Typography className={classes.title}>
              Repository Business Ownership
            </Typography>
            <Typography className={classes.subtitle}>
              These are repository business ownership suggestions determined
              based on your team's recent activity, membership, and contribution
              patterns. Please review these predictions and confirm the correct
              ownership for each repository.
            </Typography>
          </Box>
          <Typography className={classes.emptyText}>
            No repositories pending ownership confirmation for your teams.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            Repository Business Ownership
          </Typography>
          <Typography className={classes.subtitle}>
            These are repository business ownership suggestions determined based
            on your team's recent activity, membership, and contribution
            patterns. Please review these predictions and confirm the correct
            ownership for each repository.
          </Typography>
        </Box>

        <Box className={classes.statsSection}>
          <Box className={classes.statsLeft}>
            <Typography className={classes.statsText}>
              Recommendations
            </Typography>
            <Box className={classes.statsCount}>{repos.length}</Box>
          </Box>
          {selectedItems.size > 0 && (
            <Box>
              <Button
                className={classes.bulkReviseButton}
                variant="outlined"
                onClick={handleBulkRevise}
              >
                Revise {selectedItems.size} recommendation
                {selectedItems.size > 1 ? 's' : ''}
              </Button>
              <Button
                className={classes.bulkAcceptButton}
                variant="contained"
                onClick={handleBulkAccept}
              >
                Accept {selectedItems.size} recommendation
                {selectedItems.size > 1 ? 's' : ''}
              </Button>
            </Box>
          )}
        </Box>

        {/* Filter Section */}
        <Box className={classes.filterSection}>
          <TextField
            className={classes.searchField}
            placeholder="Search repositories..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel>Teams</InputLabel>
            <Select
              multiple
              value={selectedTeams}
              onChange={e => {
                setSelectedTeams(e.target.value as string[]);
                setPage(0);
              }}
              label="Teams"
              renderValue={selected => (
                <Box className={classes.filterChips}>
                  {(selected as string[]).map(value => (
                    <Chip
                      key={value}
                      label={value}
                      size="small"
                      className={classes.filterChip}
                    />
                  ))}
                </Box>
              )}
            >
              {availableTeams.map(team => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(searchQuery || selectedTeams.length > 0) && (
            <Button
              size="small"
              onClick={handleClearFilters}
              startIcon={<FilterListIcon />}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Results count */}
        {filteredRepos.length !== repos.length && (
          <Box style={{ marginBottom: 16 }}>
            <Typography variant="body2" color="textSecondary">
              Showing {filteredRepos.length} of {repos.length} recommendations
            </Typography>
          </Box>
        )}

        <Box className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.tableHeaderCell} style={{ width: 50 }}>
                  <Checkbox
                    className={classes.checkbox}
                    checked={allCurrentPageSelected}
                    indeterminate={someCurrentPageSelected}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className={classes.tableHeaderCell}>Entity</th>
                <th className={classes.tableHeaderCell}>
                  <Box
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    Proposed Team
                    <Tooltip
                      title="Hover over team names to see who proposed this recommendation"
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        style={{ fontSize: 14, opacity: 0.7 }}
                      />
                    </Tooltip>
                  </Box>
                </th>
                <th
                  className={classes.tableHeaderCell}
                  style={{ width: 120 }}
                />
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(item => (
                <tr key={item.id} className={classes.tableRow}>
                  <td className={classes.tableCell}>
                    <Checkbox
                      className={classes.checkbox}
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </td>
                  <td className={classes.tableCell}>
                    <Box className={classes.entityCell}>
                      <Box className={classes.entityIcon}>
                        {item.entity.substring(0, 2).toUpperCase()}
                      </Box>
                      <Box className={classes.entityInfo}>
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                          <Link
                            href={buildCatalogUrl(item.entityRef)}
                            className={classes.entityLink}
                          >
                            {item.entity}
                          </Link>
                          <Tooltip
                            title="Open repository in GitHub"
                            placement="top"
                          >
                            <IconButton
                              className={classes.entityLinkIcon}
                              size="small"
                              href={`https://github.com/tomtom-internal/${item.repositoryName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              component="a"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </td>
                  <td className={classes.tableCell}>
                    <Tooltip
                      title={getTooltipText(
                        item.source,
                        item.proposedByEmail,
                        item.recommendationType,
                      )}
                      placement="top"
                    >
                      <Box className={classes.teamTag}>
                        {item.teamRecommendation}
                      </Box>
                    </Tooltip>
                  </td>
                  <td className={classes.tableCell}>
                    <Box className={classes.actionButtons}>
                      <Button
                        className={classes.reviseButton}
                        onClick={() => handleRevise(item.id)}
                      >
                        Revise
                      </Button>
                      <Button
                        className={classes.acceptButton}
                        onClick={() => handleAccept(item.id)}
                      >
                        Accept
                      </Button>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Box className={classes.paginationContainer}>
            <TablePagination
              className={classes.pagination}
              component="div"
              count={filteredRepos.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Box>
      </CardContent>

      <ReviseOwnershipModal
        open={reviseModalOpen}
        onClose={handleCloseReviseModal}
        repositories={repositoriesToRevise}
        onSuccess={handleRevisionSuccess}
      />

      <ManageOwnershipDialog
        open={acceptDialogOpen}
        entities={repositoriesToAccept.map(proposedRepoToOwnershipEntity)}
        onClose={handleCloseAcceptDialog}
        onSuccess={handleAcceptSuccess}
        deleteMode="remove"
      />
    </Card>
  );
};
